import React, { Component } from 'react'
import ReactGA from 'react-ga'
import { Router, Route, Redirect, Switch } from 'react-router-dom'
import { connect, Provider } from 'react-redux'
import { bindActionCreators } from 'redux'
import { history } from '../store/configure.store'
import PropTypes from 'prop-types'
import { config } from '../config'

// Services
import Auth from '../services/auth.service'
import PusherService from '../services/pusher.service'

// Actions
import {
  setProfile,
  createToast,
  deleteToast,
  handlePusherMessage,
  CONTACT_MATCH_PUSHER_MESSAGE,
  statusType
} from '../actions'

// Pages
import DashboardPage from './dashboard/dashboard.page'
import ContactLandingPage from './contact/landing/landing.page'
import ContactDetailPage from './contact/detail/detail.page'
import CustomContactLandingPage from './custom/landing/landing.page'
import CustomContactDetailPage from './custom/detail/detail.page'
import ErrorPage from './error/error.page'
import Login from './login/login.page'
import ChangeRequestLandingPage from './changeRequest/landing/landing.page'

// Components
import { Header, Navigation, Footer, Toast, ModalProvider } from '../components'

// Utils
import { getClassName } from '../utils'

import './index.scss'

const auth = new Auth()

const PrivateRoute = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={
        (props) => {
          if (auth.isAuthenticated()) {
            return (
              <div id='app-scrollable' className='app-scrollable'>
                <Component {...props} />
                <Footer />
              </div>
            )
          }
          return <Redirect to={'/'} />
        }
      } />
  )
}

/**
 * Application
 */
class Application extends Component {

  /**
   * Constructor
   * @param props
   */
  constructor (props) {
    super(props)
    this.state = {
      navigationExpanded: false
    }
  }

  /**
   * Component did mount
   */
  componentWillMount () {
    const { setProfile } = this.props

    const gaOptions = { appName: 'Q4 Contact Admin' }

    if (!auth.isAuthenticated()) {
      setProfile()
    }

    auth.onAuthenticate = (profile, token) => {      
        setProfile(profile, token)  
        this.initPusher(profile, token)
    }

    ReactGA.initialize(config.googleAnalytics, {
      debug: process.env.REACT_APP_ENV === 'dev',
      gaOptions
    })    

    ReactGA.pageview(history.location.pathname)
    history.listen((location) => ReactGA.pageview(location.pathname))
  }

  /**
   * Initialize Pusher
   * @param profile 
   */
  initPusher (profile, token) {
    const {handlePusherMessage} = this.props
    const userId = profile && profile.user_id && profile.user_id.split('|')[1]
    this.pusher = userId && new PusherService({userId, token})
    this.pusher && this.pusher.subscribeToEvent(CONTACT_MATCH_PUSHER_MESSAGE, (message) => {
      return handlePusherMessage(CONTACT_MATCH_PUSHER_MESSAGE, message)
    })
  }

  /**
   * Expand/Collapse Navigation
   * @param event
   * @param expand - true/false to override
   */
  onNavigationToggle = (event, expand) => {
    this.setState({
      navigationExpanded: (expand !== undefined) ? expand : !this.state.navigationExpanded
    })
  }

  /**
   * Render
   * @return { XML }
   */
  render () {
    const { store, profile, token, toast, profileStatus, deleteToast } = this.props
    const { navigationExpanded } = this.state

    const baseClassName = getClassName('root-container', [
      { condition: navigationExpanded, trueClassName: 'root-container--nav-expanded' }
    ])

    return (
      <div className={baseClassName}>
        <Provider store={store}>
          <Router history={history}>
            {(!token || !token.length) && profileStatus === statusType.SUCCESS
              ? <Login auth={auth} history={history} location={history.location} />
              : <>
                <Navigation
                  navigationExpanded={navigationExpanded}
                  onNavigationToggle={this.onNavigationToggle} />

                <div className='app-body'>
                  <Header
                    profile={profile}
                    history={history}
                    auth={auth} />
                  <Switch>
                    <PrivateRoute exact path='/dashboard' component={DashboardPage} />
                    <PrivateRoute exact path='/contact' component={ContactLandingPage} />
                    <PrivateRoute path='/contact/:id' component={ContactDetailPage} />
                    <PrivateRoute exact path='/custom/security' component={CustomContactLandingPage} />
                    <PrivateRoute path='/custom/security/:id' component={CustomContactDetailPage} />
                    <PrivateRoute path='/changeRequest' component={ChangeRequestLandingPage} />
                    <PrivateRoute exact path='/error/:code' component={ErrorPage} />
                    <Route path='/' render={(props) => <Login auth={auth} {...props} />} />
                  </Switch>
                </div>

                <Toast message={toast} deleteToast={deleteToast} />
                <ModalProvider />
              </>}
          </Router>
        </Provider>
      </div>
    )
  }
}

Application.propTypes = {
  store: PropTypes.object.isRequired
}

/**
 * Maps App Store State to Container's Props
 * @param state - store state
 */
const mapStateToProps = (state) => {
  return {
    profile: state.profile.data,
    token: state.profile.token,
    profileStatus: state.profile.status,
    toast: state.toast.data
  }
}

const mapDispatchToProps = (dispatch) => ({
  setProfile: bindActionCreators(setProfile, dispatch),
  createToast: bindActionCreators(createToast, dispatch),
  deleteToast: bindActionCreators(deleteToast, dispatch),
  handlePusherMessage: bindActionCreators(handlePusherMessage, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(Application)
